/**
 * connectChatCable — ActionCable for Team Chat via shared ChatCableClient.
 *
 * Cookie session auth (same-origin /cable). Forever reconnect + ping watchdog
 * handled by @tshmanager/chat-shared. Frame dispatch via routeFrame.
 */

import { batch } from '@preact/signals'
import {
  ChatCableClient,
  CablePhase,
  routeFrame,
  createPresenceHeartbeat,
  buildPersonChannelParams,
  buildPresenceChannelParams,
  buildRoomChannelParams,
  buildTypingChannelParams,
  channelIdentifier,
  buildPingPerformData,
  projectWatermarksCamel,
  mergeMemberWatermark,
  ACTION_STOP_TYPING,
} from '@tshmanager/chat-shared'
import { applyRoomDeleted } from './leave-room.js'
import { chatApi } from './chat-api.js'
import {
  isReceiptBuffering,
  bufferReceiptSnapshot,
} from './chat-store.js'

function webCableUrl() {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${window.location.host}/cable`
}

// ── Store mutators (handlers) ─────────────────────────────────────────────────

function handleRoomRead(store, roomId) {
  batch(() => {
    store.rooms.value = store.rooms.value.map(r =>
      r.id === roomId ? { ...r, unread: 0, unreadAt: null } : r,
    )
  })
}

function handleUnreadUpdate(store, roomId) {
  batch(() => {
    store.rooms.value = store.rooms.value.map(r =>
      r.id === roomId ? { ...r, unread: (r.unread ?? 0) + 1 } : r,
    )
  })
}

function handleRoomCreated(store, room) {
  const r = { ...room }
  delete r.event
  if (!r.id) return
  batch(() => {
    if (store.rooms.value.some(x => x.id === r.id)) return
    store.rooms.value = [r, ...store.rooms.value]
  })
}

function handleRoomDeleted(store, cable, roomId) {
  applyRoomDeleted(store, cable, roomId)
}

function handlePresenceUpdate(store, payload) {
  batch(() => {
    store.presenceMap.value = {
      ...store.presenceMap.value,
      [payload.person_id]: {
        online: payload.online,
        lastActiveAt: payload.last_active_at ?? null,
      },
    }
  })
}

function handleMessageCreated(store, roomId, msg) {
  if (!msg?.id) return
  batch(() => {
    const existing = store.messagesByRoom.value[roomId] ?? []
    const clientId = msg.client_message_id ?? msg._clientId
    if (clientId) {
      store.pendingMsgs.value = store.pendingMsgs.value.filter(
        m => m._clientId !== clientId && m.client_message_id !== clientId,
      )
    }
    if (!existing.find(m => m.id === msg.id)) {
      store.messagesByRoom.value = {
        ...store.messagesByRoom.value,
        [roomId]: [...existing, msg],
      }
    }
    const isActiveRoom = store.activeRoomId.value === roomId
    if (msg.creator_person?.id !== store.personId && !isActiveRoom) {
      store.rooms.value = store.rooms.value.map(r =>
        r.id === roomId
          ? { ...r, lastMessage: msg, unread: (r.unread ?? 0) + 1 }
          : r,
      )
    } else {
      store.rooms.value = store.rooms.value.map(r =>
        r.id === roomId ? { ...r, lastMessage: msg } : r,
      )
    }
  })
}

function handleMessageUpdated(store, roomId, msg) {
  if (!msg?.id) return
  batch(() => {
    const existing = store.messagesByRoom.value[roomId] ?? []
    store.messagesByRoom.value = {
      ...store.messagesByRoom.value,
      [roomId]: existing.map(m => (m.id === msg.id ? { ...m, ...msg } : m)),
    }
  })
}

function handleMessageDeleted(store, roomId, message) {
  const msgId = message.message_id ?? message.id
  if (!msgId) return
  batch(() => {
    const existing = store.messagesByRoom.value[roomId] ?? []
    store.messagesByRoom.value = {
      ...store.messagesByRoom.value,
      [roomId]: existing.filter(m => m.id !== msgId),
    }
  })
}

function handleReactionUpdated(store, messageId, reactions) {
  if (!messageId) return
  batch(() => {
    store.reactionsById.value = {
      ...store.reactionsById.value,
      [messageId]: reactions ?? [],
    }
  })
}

function applyWatermarksToReceipts(store) {
  const watermarks = store.memberWatermarks.value
  const messages = store.currentMessages.value
  const myPersonId = store.personId
  if (!messages.length || !Object.keys(watermarks).length) return

  const eligible = messages.filter(m => m.id && m.created_at)
  const projected = projectWatermarksCamel(watermarks, eligible, myPersonId)
  batch(() => {
    store.receiptsById.value = projected
  })
}

function handleReceiptSnapshot(store, data, subscribedRoomId) {
  if (data.room_id !== subscribedRoomId) return
  if (isReceiptBuffering(data.room_id)) {
    bufferReceiptSnapshot(data.room_id, data)
    return
  }
  const { members } = data
  if (!members?.length) return
  batch(() => {
    const updated = { ...store.memberWatermarks.value }
    for (const entry of members) {
      const person = entry.person
      if (!person?.id) continue
      mergeMemberWatermark(updated, {
        person,
        lastReadAt: entry.last_read_at ?? null,
        lastDeliveredAt: entry.last_delivered_at ?? null,
      }, store.personId)
    }
    store.memberWatermarks.value = updated
  })
  applyWatermarksToReceipts(store)
}

function handleMemberReceiptUpdate(store, payload, subscribedRoomId) {
  if (payload.room_id !== subscribedRoomId) return
  batch(() => {
    const updated = { ...store.memberWatermarks.value }
    mergeMemberWatermark(updated, {
      person: payload.person,
      lastReadAt: payload.last_read_at ?? null,
      lastDeliveredAt: payload.last_delivered_at ?? null,
    }, store.personId)
    store.memberWatermarks.value = updated
  })
  applyWatermarksToReceipts(store)
}

function handleTyping(store, roomId, payload) {
  if (payload.person_id === store.personId) return
  batch(() => {
    const current = store.typingByRoom.value[roomId] ?? []
    if (payload.typing) {
      store.typingByRoom.value = {
        ...store.typingByRoom.value,
        [roomId]: [
          ...current.filter(t => t.personId !== payload.person_id),
          {
            personId: payload.person_id,
            name: payload.person_name,
            avatarUrl: payload.avatar_url ?? null,
          },
        ],
      }
    } else {
      store.typingByRoom.value = {
        ...store.typingByRoom.value,
        [roomId]: current.filter(t => t.personId !== payload.person_id),
      }
    }
  })
}

async function catchUpAfterReconnect(store, subscribedRoomId) {
  try {
    const roomsRes = await chatApi.rooms.list()
    if (roomsRes.ok) {
      const data = await roomsRes.json
      const list = Array.isArray(data) ? data : (data.rooms ?? [])
      batch(() => { store.rooms.value = list })
    }
  } catch { /* non-fatal */ }

  const roomId = subscribedRoomId ?? store.activeRoomId.value
  if (!roomId) return
  try {
    const res = await chatApi.messages.list(roomId)
    if (res.ok) {
      const data = await res.json
      const msgs = data.messages ?? data ?? []
      batch(() => {
        store.messagesByRoom.value = {
          ...store.messagesByRoom.value,
          [roomId]: msgs,
        }
      })
      applyWatermarksToReceipts(store)
    }
  } catch { /* non-fatal */ }
}

export { applyWatermarksToReceipts }

export function connectChatCable({ store, personId, tenantId }) {
  let client = null
  let heartbeat = null
  let subscribedRoomId = null
  /** @type {{ subscribeRoom: Function, unsubscribeRoom: Function, disconnect: Function }} */
  const cableRef = {}
  const personParams = buildPersonChannelParams(personId, tenantId)
  const presenceParams = buildPresenceChannelParams(tenantId)
  const personIdentifier = channelIdentifier(personParams)
  const presenceIdentifier = channelIdentifier(presenceParams)

  function subscribeGlobals(ws) {
    ws.send(JSON.stringify({ command: 'subscribe', identifier: personIdentifier }))
    ws.send(JSON.stringify({ command: 'subscribe', identifier: presenceIdentifier }))
  }

  function resubscribeRoom(ws) {
    if (subscribedRoomId == null) return
    const roomParams = buildRoomChannelParams(subscribedRoomId, tenantId)
    const typingParams = buildTypingChannelParams(subscribedRoomId, tenantId)
    ws.send(JSON.stringify({ command: 'subscribe', identifier: channelIdentifier(roomParams) }))
    ws.send(JSON.stringify({ command: 'subscribe', identifier: channelIdentifier(typingParams) }))
  }

  const adapter = {
    async getCableUrl() {
      return webCableUrl()
    },

    onPhaseChange(phase) {
      store.cablePhase.value = phase
    },

    onConnected(isReconnect) {
      const ws = client?.socket
      if (!ws || ws.readyState !== WebSocket.OPEN) return
      subscribeGlobals(ws)
      resubscribeRoom(ws)
      heartbeat?.pingNow()
      if (isReconnect) {
        void catchUpAfterReconnect(store, subscribedRoomId)
      }
    },

    onFrame(data) {
      const handlers = {
        onRoomRead: (roomId) => handleRoomRead(store, roomId),
        onUnreadUpdate: (roomId) => handleUnreadUpdate(store, roomId),
        onRoomCreated: (room) => handleRoomCreated(store, room),
        onRoomDeleted: (roomId) => handleRoomDeleted(store, cableRef, roomId),
        onPresenceUpdate: (_tid, payload) => handlePresenceUpdate(store, payload),
        onMessageCreated: (roomId, msg) => handleMessageCreated(store, roomId, msg),
        onMessageUpdated: (roomId, msg) => handleMessageUpdated(store, roomId, msg),
        onMessageDeleted: (roomId, msg) => handleMessageDeleted(store, roomId, msg),
        onReactionUpdated: (_roomId, messageId, reactions) =>
          handleReactionUpdated(store, messageId, reactions),
        onReceiptSnapshot: (payload) =>
          handleReceiptSnapshot(store, payload, subscribedRoomId),
        onMemberReceiptUpdate: (payload) =>
          handleMemberReceiptUpdate(store, payload, subscribedRoomId),
        onTyping: (roomId, payload) => handleTyping(store, roomId, payload),
      }
      routeFrame(data, subscribedRoomId, tenantId, handlers)
    },
  }

  client = new ChatCableClient('web-chat', 0, adapter)

  heartbeat = createPresenceHeartbeat({
    sendPing() {
      const ws = client?.socket
      if (!ws || ws.readyState !== WebSocket.OPEN) return
      ws.send(JSON.stringify({
        command: 'message',
        identifier: presenceIdentifier,
        data: buildPingPerformData(),
      }))
    },
    shouldRun: () => !document.hidden,
  })

  function onVisibility() {
    if (document.hidden) {
      heartbeat.setPaused(true)
    } else {
      heartbeat.setPaused(false)
      if (client?.socket?.readyState === WebSocket.CLOSED) {
        client.resume()
      }
    }
  }

  function onOnline() {
    if (client?.socket?.readyState === WebSocket.CLOSED) {
      client.resume()
    }
  }

  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('online', onOnline)

  const cableApi = {
    subscribeRoom(roomId) {
      if (subscribedRoomId === roomId) return
      const ws = client?.socket
      if (subscribedRoomId != null && ws?.readyState === WebSocket.OPEN) {
        const prevRoomParams = buildRoomChannelParams(subscribedRoomId, tenantId)
        const prevTypingParams = buildTypingChannelParams(subscribedRoomId, tenantId)
        client.perform(
          prevTypingParams.channel,
          { room_id: subscribedRoomId, tenant_id: tenantId },
          ACTION_STOP_TYPING,
          { room_id: subscribedRoomId },
        )
        client.unsubscribe(prevTypingParams.channel, { room_id: subscribedRoomId, tenant_id: tenantId })
        client.unsubscribe(prevRoomParams.channel, { room_id: subscribedRoomId, tenant_id: tenantId })
      }
      subscribedRoomId = roomId
      batch(() => {
        store.memberWatermarks.value = {}
        store.receiptsById.value = {}
      })
      if (ws?.readyState === WebSocket.OPEN) {
        const roomParams = buildRoomChannelParams(roomId, tenantId)
        const typingParams = buildTypingChannelParams(roomId, tenantId)
        ws.send(JSON.stringify({ command: 'subscribe', identifier: channelIdentifier(roomParams) }))
        ws.send(JSON.stringify({ command: 'subscribe', identifier: channelIdentifier(typingParams) }))
      }
    },

    unsubscribeRoom(roomId) {
      if (subscribedRoomId !== roomId) return
      const ws = client?.socket
      if (ws?.readyState === WebSocket.OPEN) {
        const roomParams = buildRoomChannelParams(roomId, tenantId)
        const typingParams = buildTypingChannelParams(roomId, tenantId)
        client.perform(
          typingParams.channel,
          { room_id: roomId, tenant_id: tenantId },
          ACTION_STOP_TYPING,
          { room_id: roomId },
        )
        client.unsubscribe(typingParams.channel, { room_id: roomId, tenant_id: tenantId })
        client.unsubscribe(roomParams.channel, { room_id: roomId, tenant_id: tenantId })
      }
      subscribedRoomId = null
      batch(() => {
        store.memberWatermarks.value = {}
        store.receiptsById.value = {}
      })
    },

    disconnect() {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('online', onOnline)
      heartbeat.stop()
      if (subscribedRoomId != null) {
        cableApi.unsubscribeRoom(subscribedRoomId)
      }
      client.stop()
      store.cablePhase.value = CablePhase.Idle
    },

    get client() {
      return client
    },
  }

  Object.assign(cableRef, cableApi)
  client.start()
  heartbeat.start()
  return cableApi
}
